// Phase-3 Slice 34 — open-tab candidate scan.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 34 + § NAV1 (amended
// 2026-08-23).
//
// Walks each open query tab's SQL after a successful rename and produces a
// `TabCourtesyNotice[]` for every tab that mentions the old identifier.
//
// The scanner is name-only — it matches by folded identifier name, not by
// scope-resolved binding — and that is deliberate: it is CHEAP, so it decides
// which buffers are worth the full parse. Under the NAV1 amendment its output
// is the candidate list `renamePostApplyTabPass` hands to `planTabRewrite`,
// which re-derives the occurrences precisely (scope-aware, NAV10) before
// anything is edited. A false positive here therefore costs one wasted parse,
// never a wrong edit.
//
// Comments + string literals + dollar-tagged blocks are skipped via the shared
// `forEachIdentifierChain` walk (same `trySkipNonCode` primitives the rename
// planners use), so identifier-shaped text inside `--` / `/* */` / `'…'` /
// `$tag$ … $tag$` regions never produces a candidate.
//
// For column rename: matches when `qn.foldedName` equals the renamed
// column's folded name (any qualifier — `id`, `users.id`, `u.id` all match).
// For table/view rename: matches the same trailing-name slot AND any
// schema-qualifier slot (so `users.id` matches when renaming table
// `users`).

import type { DatabaseEngineId } from '@/core/engines';

import { buildLineIndex, rangeFromOffsets } from '../../sql-grammar/document/lineIndex';
import type { ParseOptions, SqlRange } from '../../sql-grammar/model';
import { databaseEngineIdToSqlEngine } from '../../intellisense/versionGating/engineIdMapping';

import type { QualifiedName, RenameKind, TabCourtesyNotice } from '../types';

import { forEachIdentifierChain } from './bufferIdentifierScan';

export type ScanTabInput = {
  readonly tabId: string;
  readonly sql: string;
  readonly engineId: DatabaseEngineId;
};

export function scanTabsForRenamedIdentifier(
  oldName: QualifiedName,
  newName: QualifiedName,
  kind: RenameKind,
  tabs: readonly ScanTabInput[],
): TabCourtesyNotice[] {
  const oldFolded = (oldName.foldedName ?? oldName.name).toLowerCase();
  if (!oldFolded) return [];

  const matchSchemaSlot = kind === 'table' || kind === 'view';
  const out: TabCourtesyNotice[] = [];

  for (const tab of tabs) {
    if (!tab.sql) continue;
    const sqlEngine = databaseEngineIdToSqlEngine(tab.engineId);
    if (!sqlEngine) continue;
    const ranges = scanSingleTab(
      tab.sql,
      oldFolded,
      matchSchemaSlot,
      { engine: sqlEngine },
    );
    if (ranges.length === 0) continue;
    out.push({
      tabId: tab.tabId,
      oldName,
      newName,
      kind,
      references: ranges,
    });
  }
  return out;
}

function scanSingleTab(
  source: string,
  oldFolded: string,
  matchSchemaSlot: boolean,
  options: ParseOptions,
): SqlRange[] {
  const lineIdx = buildLineIndex(source);
  const ranges: SqlRange[] = [];
  forEachIdentifierChain(source, options, 0, source.length, (chain) => {
    const qn = chain.qualified;
    const trailing = (qn.foldedName ?? qn.name).toLowerCase();
    const schemaPart = (qn.foldedSchema ?? qn.schema ?? '').toLowerCase();
    if (trailing === oldFolded || (matchSchemaSlot && schemaPart === oldFolded)) {
      ranges.push(rangeFromOffsets(lineIdx, chain.start, chain.end));
    }
  });
  return ranges;
}
